import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { fetchCompanies } from "../../services/api/company";
import { fetchCustomers } from "../../services/api/admin";
import BaseAutoSelect from "../../components/ui/BaseAutoSelect";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseAsyncCitySelect from "../../components/ui/BaseAsyncCitySelect";
import { useCountries } from "../../hooks/useCountries";
import { resetAdminFilters, setAdminFilters } from "../../store/filters/adminFiltersSlice";
import BaseDrawer from "../../components/ui/BaseDrawer";
import { profileStatusOptions } from "../../utils/variables";
import dayjs from "dayjs";
import BaseDatePicker from "../../components/ui/BaseDatePicker";

const AdminFilters = ({
  open,
  onClose,
  fetchData,
  isLoading,
  setFiltersChanged,
  entity = "",
  nameOptions = null,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.adminFilters[entity]);
  const { countries } = useCountries();
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [localFilters, setLocalFilters] = useState({
    fullName: "",
    email: "",
    city: { value: "", label: "" },
    country: { value: "", label: "" },
    status: "",
    manager: "",
    role: "",
    companyType: "",
    startDate: "",
    endDate: "",
  });

  const { data: vendorsData } = useQuery({
    queryKey: ["vendorFilterOptions"],
    queryFn: () => fetchCompanies({ companyType: "FREELANCE" }),
    enabled: entity === "company" && open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: customersData } = useQuery({
    queryKey: ["customerFilterOptions"],
    queryFn: () => fetchCustomers({ limit: 500 }),
    enabled: entity === "customer" && open,
    staleTime: 5 * 60 * 1000,
  });

  const customerNameOptions = useMemo(
    () =>
      [...new Set((customersData?.customers || []).map((c) => c.clientName).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [customersData],
  );

  const { vendorNameOptions, createdByOptions } = useMemo(() => {
    const names = new Set();
    const owners = new Set();
    (vendorsData?.companies || []).forEach((company) => {
      if (company.name) names.add(company.name);
      const owner = `${company.owner?.firstName || ""} ${company.owner?.lastName || ""}`.trim();
      if (owner) owners.add(owner);
    });
    const toOptions = (set) =>
      [...set].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
    return { vendorNameOptions: toOptions(names), createdByOptions: toOptions(owners) };
  }, [vendorsData]);

  useEffect(() => {
    if (filters) {
      setLocalFilters((prev) => ({ ...prev, ...filters }));
    }
  }, [filters]);

  useEffect(() => {
    if (filtersApplied) {
      fetchData();
      onClose();
      setFiltersApplied(false);
    }
  }, [filtersApplied, fetchData, onClose]);

  const handleApplyFilters = () => {
    Object.entries(localFilters).forEach(([key, val]) => {
      const cleanValue = val?.target ? val.target : val;
      dispatch(setAdminFilters({ module: entity, filterName: key, data: cleanValue }));
    });

    const newPagination = { page: 0, pageSize: filters.pagination?.pageSize || 20 };
    dispatch(setAdminFilters({ module: entity, filterName: "pagination", data: newPagination }));

    setFiltersApplied(true);
  };

  const handleResetFilters = () => {
    dispatch(resetAdminFilters({ module: entity }));
    setLocalFilters({
      fullName: "",
      email: "",
      city: { value: "", label: "" },
      country: { value: "", label: "" },
      status: "",
      manager: "",
      role: "",
      startDate: "",
      endDate: "",
    });
    setFiltersChanged(true);
  };

  const hasFilters = Object.values(localFilters).some((v) => (typeof v === "object" ? v.value : v));

  const handleCountryChange = (option) => {
    setLocalFilters((prev) => ({
      ...prev,
      country: { value: option.target?.value, label: option.target?.label },
      city: { value: "", label: "" },
    }));
  };

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={handleApplyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isLoading}
      enabled={hasFilters}
      handleResetFilters={handleResetFilters}
    >
      <div>
        {entity === "engineer" && (
          <div className="mb-4 grid grid-cols-2 gap-4 items-center">
            <div>
              <label className="block text-sm text-[#686868]">Start Date</label>
              <BaseDatePicker
                className="border-none w-full"
                type="date"
                id="startDate"
                name="startDate"
                maxDate={localFilters.endDate ? dayjs(localFilters.endDate) : undefined}
                value={localFilters.startDate ? dayjs(localFilters.startDate) : null}
                onChange={(event) => {
                  const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                  setLocalFilters({ ...localFilters, startDate: selectedDate });
                }}
                slotProps={{
                  textField: { sx: { ...datePickerStyles } },
                }}
              />
            </div>

            <div>
              <label className="block text-sm text-[#686868]">End Date</label>
              <BaseDatePicker
                className="border-none"
                type="date"
                id="endDate"
                name="endDate"
                minDate={localFilters.startDate ? dayjs(localFilters.startDate) : undefined}
                value={localFilters.endDate ? dayjs(localFilters.endDate) : null}
                onChange={(event) => {
                  const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                  setLocalFilters({ ...localFilters, endDate: selectedDate });
                }}
                slotProps={{
                  textField: { sx: { ...datePickerStyles } },
                }}
              />
            </div>
          </div>
        )}
        <div className="mb-5">
          {entity === "company" || entity === "customer" ? (
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Full Name"
              name={entity === "customer" ? "customerName" : "companyName"}
              placeholder="Select Full Name"
              options={entity === "customer" ? customerNameOptions : vendorNameOptions}
              value={
                (entity === "customer" ? localFilters.customerName : localFilters.companyName) || ""
              }
              onChange={(option) =>
                setLocalFilters({
                  ...localFilters,
                  ...(entity === "customer"
                    ? { customerName: option.target.value }
                    : { companyName: option.target.value }),
                })
              }
              isClearable
            />
          ) : nameOptions ? (
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Full Name"
              name="fullName"
              placeholder="Select Full Name"
              options={nameOptions}
              value={localFilters.fullName || ""}
              onChange={(option) =>
                setLocalFilters({ ...localFilters, fullName: option.target.value })
              }
              isClearable
            />
          ) : (
            <BaseTextField
              optional={false}
              required={true}
              labelShrink={false}
              label="Full Name"
              name="filterFullName"
              type="search"
              autoComplete="off"
              placeholder="Enter Full Name"
              value={localFilters.fullName}
              onChange={(e) => setLocalFilters({ ...localFilters, fullName: e.target.value })}
              isClearable
              className="!bg-white !rounded-md !border-none !shadow-[0_1px_3px_rgba(0,0,0,0.2)] focus:!shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-shadow duration-200"
              InputProps={{
                sx: {
                  border: "none",
                  boxShadow: "none",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          )}
        </div>

        {entity !== "company" && (
          <div className="mb-5">
            <BaseTextField
              optional={false}
              labelShrink={false}
              label="Email"
              name="filterEmail"
              type="search"
              autoComplete="off"
              placeholder="Enter Email"
              value={localFilters.email}
              onChange={(e) => setLocalFilters({ ...localFilters, email: e.target.value })}
              isClearable
              className="!bg-white !rounded-md !border-none !shadow-[0_1px_3px_rgba(0,0,0,0.2)] focus:!shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-shadow duration-200"
              InputProps={{
                sx: {
                  border: "none",
                  boxShadow: "none",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </div>
        )}

        {entity !== "freelanceCompanyMembers" && (
          <div className="mb-5">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Country"
              name="country"
              placeholder="Select Country"
              options={countries || []}
              value={localFilters.country?.value || ""}
              onChange={handleCountryChange}
              isClearable
            />
          </div>
        )}

        {entity !== "companyUsers" &&
          entity !== "customerAccountUsers" &&
          entity !== "freelanceCompanyMembers" && (
            <div className="mb-5">
              <BaseAsyncCitySelect
                required={true}
                labelShrink={false}
                label="City"
                placeholder="Enter City"
                country={localFilters.country?.label}
                value={localFilters.city?.value || ""}
                onChange={(city) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    city: city ? { value: city, label: city } : { value: "", label: "" },
                  }))
                }
              />
            </div>
          )}
        {entity === "company" && (
          <div className="mb-5">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Created By"
              name="manager"
              placeholder="Select Created By"
              options={createdByOptions}
              value={localFilters.manager || ""}
              onChange={(option) =>
                setLocalFilters({ ...localFilters, manager: option.target.value })
              }
              isClearable
            />
          </div>
        )}
        {entity === "companyUsers" && (
          <div className="mb-5">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Role"
              name="role"
              placeholder="Select Role"
              options={[
                { value: "Manager", label: "Manager" },
                { value: "Admin", label: "Admin" },
                { value: "Supervisor", label: "Supervisor" },
                { value: "Coordinator", label: "Coordinator" },
              ]}
              value={localFilters.role}
              onChange={(option) => setLocalFilters({ ...localFilters, role: option.target.value })}
              isClearable
            />
          </div>
        )}

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Status"
            name="status"
            placeholder="Select Status"
            options={profileStatusOptions}
            value={localFilters.status}
            onChange={(option) => setLocalFilters({ ...localFilters, status: option.target.value })}
            isClearable
          />
        </div>
      </div>
    </BaseDrawer>
  );
};
const datePickerStyles = {
  "& .MuiOutlinedInput-root": {
    height: "42px",
    borderRadius: "5px",
    backgroundColor: "white",
    boxShadow: "1px 1px 4px 1px #29292921",
    display: "flex",
    alignItems: "center",
    margin: "6px 1.5px",
    border: "none !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
    boxShadow: "none",
  },
  "& .MuiInputBase-input": {
    padding: "4px 10px",
    fontSize: "13px",
    height: "18px",
    lineHeight: "1.4",
  },
  "& .MuiIconButton-root": {
    padding: "4px",
    backgroundColor: "white !important",
    borderLeft: "none !important",
  },
};
export default AdminFilters;
